import { useEffect, useState } from "react";
import {
    Box,
    Flex,
    Text,
    VStack,
    HStack,
    Image,
    Skeleton,
    Tooltip,
    IconButton,
    Divider,
} from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { ThemeColor } from "../../theme/palette";
import { formatSmartFixed6 } from "../../utils/format";
import { DripPosition } from "../../hooks/useDripPosition";
import { DRIP_BOX_BG, DRIP_BORDER, DRIP_TEXT_LABEL, DRIP_TEXT_MUTED } from "../../features/drip/dripStyles";
import { useDripVaultIcon } from "../../features/drip/useDripVaultIcon";
import { summariseDripPositions } from "../../features/drip/aggregateDripPositions";

interface Props {
    /** Every vault the wallet holds, in registry order. */
    positions: DripPosition[];
    isLoading: boolean;
}

const formatCountdown = (seconds: number): string => {
    if (seconds <= 60) return "<1m";
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `~${days}d ${hours}h`;
    if (hours > 0) return `~${hours}h ${minutes}m`;
    return `~${minutes}m`;
};

const formatUsd = (value: number): string =>
    `~$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * One vault's line inside the card.
 *
 * A child component rather than inlined markup so each line can call
 * useDripVaultIcon once — a hook cannot be called from inside a map.
 */
const DripPositionRow = ({ position }: { position: DripPosition }) => {
    const vaultIcon = useDripVaultIcon(position.vaultId, position.depositMint);

    if (position.isValueUnavailable) {
        // Never render the 0 that an unreadable share supply produces: it would
        // tell a funded wallet it is empty.
        return (
            <Flex px={{ base: 4, sm: 6 }} py={4} align="center" justify="space-between" gap={4}>
                <VStack align="start" spacing={0}>
                    <Text fontSize={{ base: "lg", sm: "xl" }} fontWeight="semibold" color="white">
                        Unavailable
                    </Text>
                    <Text fontSize="xs" color={DRIP_TEXT_MUTED} mt={1}>
                        Couldn't read the {position.symbol} vault's share supply. Your funds are safe — refresh to retry.
                    </Text>
                </VStack>
                {position.rewardTokenName && (
                    <HStack spacing={2} flexShrink={0}>
                        {position.rewardTokenImage && (
                            <Image src={position.rewardTokenImage} boxSize="20px" borderRadius="full" />
                        )}
                        <Text fontSize={{ base: "md", sm: "lg" }} fontWeight="semibold" color={ThemeColor.KAIROS}>
                            {position.rewardTokenName}
                        </Text>
                    </HStack>
                )}
            </Flex>
        );
    }

    // tokenValue already arrives capped at what the wallet holds, so the amount
    // below is honest; this only explains why it shrank. After the unavailable
    // check on purpose — a failed read must never read as a lost claim.
    const isClaimDestroyed = position.isClaimShort;
    const holdsNothing = !(BigInt(position.withdrawableSharesRaw || "0") > BigInt(0));

    return (
        <Flex px={{ base: 4, sm: 6 }} py={4} align="center" justify="space-between" gap={4}>
            <HStack spacing={2.5} minW={0}>
                {vaultIcon && <Image src={vaultIcon} boxSize="22px" borderRadius="full" flexShrink={0} />}
                <VStack align="start" spacing={0} minW={0}>
                    <Text
                        fontSize={{ base: "lg", sm: "xl" }}
                        fontWeight="semibold"
                        color="white"
                        lineHeight="1.3"
                    >
                        {formatSmartFixed6(position.tokenValue)} {position.symbol}
                    </Text>
                    {/* The dash would just sit above the explanation below. */}
                    {position.tokenValueUsd !== null ? (
                        <Text fontSize="xs" color={DRIP_TEXT_MUTED}>
                            {formatUsd(position.tokenValueUsd)}
                        </Text>
                    ) : (
                        !isClaimDestroyed && (
                            <Text fontSize="xs" color={DRIP_TEXT_MUTED}>
                                —
                            </Text>
                        )
                    )}
                    {isClaimDestroyed && (
                        <Text fontSize="xs" color={DRIP_TEXT_MUTED} mt={1}>
                            {holdsNothing
                                ? `Your ${position.symbol} share tokens are no longer in this wallet. Holding them is the claim, so nothing is left here and this position can't be withdrawn.`
                                : `Some of your ${position.symbol} share tokens are no longer in this wallet. Only the amount still held is backed by a claim — the rest can't be withdrawn.`}
                        </Text>
                    )}
                </VStack>
            </HStack>

            <HStack spacing={2} flexShrink={0}>
                {position.rewardTokenImage && (
                    <Image src={position.rewardTokenImage} boxSize="20px" borderRadius="full" />
                )}
                <Text fontSize={{ base: "md", sm: "lg" }} fontWeight="semibold" color={ThemeColor.KAIROS}>
                    {position.rewardTokenName}
                </Text>
            </HStack>
        </Flex>
    );
};

/**
 * A wallet's whole Drip position: one card listing every vault it holds, with a
 * combined USD header and a single epoch countdown.
 *
 * One card rather than one per vault — the vaults share an epoch and a rewards
 * schedule, so N stacked cards repeated the same countdown and gave the wallet
 * no total.
 */
export const DripPositionsCard = ({ positions, isLoading }: Props) => {
    const { totalUsd, unpricedCount, nextEpochEstimateSeconds, isWarmingUp } =
        summariseDripPositions(positions);
    const [countdownSeconds, setCountdownSeconds] = useState<number | null>(
        nextEpochEstimateSeconds
    );

    useEffect(() => {
        setCountdownSeconds(nextEpochEstimateSeconds);
    }, [nextEpochEstimateSeconds]);

    useEffect(() => {
        if (countdownSeconds === null) return;
        const intervalId = window.setInterval(() => {
            setCountdownSeconds((prev) => {
                if (prev === null) return null;
                return Math.max(0, prev - 60);
            });
        }, 60000);
        return () => window.clearInterval(intervalId);
    }, [countdownSeconds]);

    // A single position already shows its own USD value on its line, so the
    // header would just repeat it.
    const showTotal = positions.length > 1;
    const isEmpty = positions.length === 0;

    return (
        <Box
            bg={DRIP_BOX_BG}
            borderRadius="xl"
            border="1px solid"
            borderColor={DRIP_BORDER}
            overflow="hidden"
            w="100%"
        >
            <Flex
                px={{ base: 4, sm: 6 }}
                py={4}
                align="flex-end"
                justify="space-between"
                gap={4}
                borderBottom="1px solid"
                borderColor="whiteAlpha.100"
            >
                <VStack align="start" spacing={showTotal ? 1 : 0}>
                    <Text fontSize="xs" fontWeight="medium" color={DRIP_TEXT_LABEL} letterSpacing="0.08em">
                        {showTotal ? "TOTAL DEPOSITED" : "DEPOSITED"}
                    </Text>
                    {showTotal &&
                        (isLoading && totalUsd === null ? (
                            <Skeleton height="28px" width="120px" />
                        ) : (
                            <HStack spacing={2} align="center">
                                <Text fontSize={{ base: "xl", sm: "2xl" }} fontWeight="semibold" color="white">
                                    {totalUsd !== null ? formatUsd(totalUsd) : "—"}
                                </Text>
                                {unpricedCount > 0 && (
                                    <Tooltip
                                        label={`Excludes ${unpricedCount} position${unpricedCount === 1 ? "" : "s"} we couldn't price right now.`}
                                        hasArrow
                                        placement="top"
                                        bg={DRIP_BOX_BG}
                                        color="white"
                                        borderRadius="md"
                                        fontSize="xs"
                                    >
                                        <IconButton
                                            aria-label="Partial total info"
                                            icon={<InfoOutlineIcon boxSize="11px" />}
                                            minW="18px"
                                            w="18px"
                                            h="18px"
                                            p={0}
                                            variant="ghost"
                                            color={DRIP_TEXT_LABEL}
                                            _hover={{ color: "white", bg: "whiteAlpha.100" }}
                                        />
                                    </Tooltip>
                                )}
                            </HStack>
                        ))}
                </VStack>

                <Text fontSize="xs" fontWeight="medium" color={DRIP_TEXT_LABEL} letterSpacing="0.08em">
                    REWARD TOKEN
                </Text>
            </Flex>

            {isLoading && isEmpty ? (
                <Flex px={{ base: 4, sm: 6 }} py={4} align="center" justify="space-between" gap={4}>
                    <Skeleton height="32px" width="150px" />
                    <Skeleton height="24px" width="70px" />
                </Flex>
            ) : (
                positions.map((position, index) => (
                    <Box key={position.vaultId}>
                        {index > 0 && <Divider borderColor="whiteAlpha.100" />}
                        <DripPositionRow position={position} />
                    </Box>
                ))
            )}

            {/* The countdown needs the epoch lookup only the enriched read does.
                A pulsing "live" dot beside "Next rewards in --" reads as broken,
                so the row is hidden until one position carries an estimate. */}
            {countdownSeconds !== null && (
                <Flex
                    justify="center"
                    px={4}
                    py={4}
                    borderTop="1px solid"
                    borderColor="whiteAlpha.100"
                >
                    <HStack
                        spacing={3}
                        align="center"
                        px={3}
                        py={1.5}
                        borderRadius="full"
                        bg="rgba(95, 244, 244, 0.08)"
                        border="1px solid"
                        borderColor="rgba(95, 244, 244, 0.25)"
                    >
                        <Box
                            boxSize="8px"
                            flexShrink={0}
                            borderRadius="full"
                            bg={ThemeColor.KAIROS}
                            animation="pulseLive 2.2s ease-in-out infinite"
                            sx={{
                                "@keyframes pulseLive": {
                                    "0%": { boxShadow: "0 0 0 0 rgba(95, 244, 244, 0.45)" },
                                    "70%": { boxShadow: "0 0 0 8px rgba(95, 244, 244, 0)" },
                                    "100%": { boxShadow: "0 0 0 0 rgba(95, 244, 244, 0)" },
                                },
                            }}
                        />
                        <Text fontSize="xs" fontWeight="semibold" color={ThemeColor.KAIROS} letterSpacing="0.02em">
                            Next rewards in {formatCountdown(countdownSeconds)}
                        </Text>
                        <Tooltip
                            label={
                                isWarmingUp
                                    ? "Your deposit is warming up. Rewards start after one full epoch."
                                    : "Rewards stream every Solana epoch directly inside your wallet."
                            }
                            hasArrow
                            placement="top"
                            bg={DRIP_BOX_BG}
                            color="white"
                            borderRadius="md"
                            fontSize="xs"
                        >
                            <IconButton
                                aria-label="Rewards streaming info"
                                icon={<InfoOutlineIcon boxSize="11px" />}
                                minW="18px"
                                w="18px"
                                h="18px"
                                p={0}
                                variant="ghost"
                                color={DRIP_TEXT_LABEL}
                                _hover={{ color: "white", bg: "whiteAlpha.100" }}
                            />
                        </Tooltip>
                    </HStack>
                </Flex>
            )}
        </Box>
    );
};
